import { BLOCK_SIZE, MATERIALS, WORLD } from './constants.js'

const SUPPORT_EPSILON = WORLD.grid * 0.13
const SIDE_EPSILON = WORLD.grid * 0.12
const MIN_SUPPORT_OVERLAP = WORLD.grid * 0.2
const MIN_SIDE_OVERLAP = WORLD.grid * 0.18
const FALL_STABILITY_THRESHOLD = 0.34
const MIN_EFFECTIVE_SUPPORT_WIDTH = WORLD.grid * 0.35
const MIN_ASPECT_QUALITY = 0.62

export function buildPhysicsGraph(items, options = {}) {
    const floorY = options.floorY ?? WORLD.groundY
    const nodes = new Map()
    const edges = []

    for (const item of items || []) {
        const node = normalizeNode(item)
        if (node?.id) {
            nodes.set(node.id, node)
        }
    }

    const nodeList = [...nodes.values()]

    for (const upper of nodeList) {
        if (Math.abs(upper.bounds.minY - floorY) <= SUPPORT_EPSILON || upper.bounds.minY <= floorY + SUPPORT_EPSILON) {
            upper.floorSupported = true
        }

        for (const lower of nodeList) {
            if (upper.id === lower.id) {
                continue
            }

            const supportGap = Math.abs(upper.bounds.minY - lower.bounds.maxY)
            const supportOverlap = getIntervalOverlap(
                upper.bounds.minX,
                upper.bounds.maxX,
                lower.bounds.minX,
                lower.bounds.maxX
            )

            if (supportGap <= SUPPORT_EPSILON && supportOverlap >= MIN_SUPPORT_OVERLAP) {
                edges.push({
                    type: 'liesOn',
                    upperId: upper.id,
                    lowerId: lower.id,
                    overlap: supportOverlap,
                    supportMinX: Math.max(upper.bounds.minX, lower.bounds.minX),
                    supportMaxX: Math.min(upper.bounds.maxX, lower.bounds.maxX)
                })
            }

            const rightGap = Math.abs(upper.bounds.maxX - lower.bounds.minX)
            const leftGap = Math.abs(upper.bounds.minX - lower.bounds.maxX)
            const sideOverlap = getIntervalOverlap(
                upper.bounds.minY,
                upper.bounds.maxY,
                lower.bounds.minY,
                lower.bounds.maxY
            )

            if (rightGap <= SIDE_EPSILON && sideOverlap >= MIN_SIDE_OVERLAP) {
                edges.push({
                    type: 'touchesRight',
                    leftId: upper.id,
                    rightId: lower.id,
                    overlap: sideOverlap
                })
            }

            if (leftGap <= SIDE_EPSILON && sideOverlap >= MIN_SIDE_OVERLAP) {
                edges.push({
                    type: 'touchesLeft',
                    rightId: upper.id,
                    leftId: lower.id,
                    overlap: sideOverlap
                })
            }
        }
    }

    return { nodes, edges, floorY }
}

export function computeStability(blockId, graph, options = {}) {
    const node = graph?.nodes?.get(blockId)

    if (!node) {
        return 0
    }

    const ignored = options.ignored || new Set()
    if (ignored.has(blockId)) {
        return 0
    }

    const supports = getSupportEdges(blockId, graph, ignored)
    const supportBase = getSupportBase(node, supports)

    if (node.floorSupported && !ignored.has(blockId)) {
        supportBase.minX = Math.min(supportBase.minX, node.bounds.minX)
        supportBase.maxX = Math.max(supportBase.maxX, node.bounds.maxX)
        supportBase.overlap += node.width
    }

    if (!Number.isFinite(supportBase.minX) || supportBase.overlap <= 0) {
        return 0
    }

    const supportQuality = clamp(supportBase.overlap / Math.max(MIN_EFFECTIVE_SUPPORT_WIDTH, node.width), 0, 1)
    const centerQuality = node.center.x >= supportBase.minX - WORLD.grid * 0.04
        && node.center.x <= supportBase.maxX + WORLD.grid * 0.04
        ? 1
        : 0.18
    const aspectQuality = clamp(Math.sqrt(node.width / Math.max(MIN_EFFECTIVE_SUPPORT_WIDTH, node.height)), MIN_ASPECT_QUALITY, 1.2)
    const material = MATERIALS[node.material] || MATERIALS.wood
    const materialQuality = getMaterialSupportQuality(material)
    const loadBonus = clamp(1 + getMassAbove(blockId, graph, ignored) * 0.055, 1, 1.28)
    const braceBonus = clamp(1 + getSideBraceMass(blockId, graph, ignored) * 0.038, 1, 1.22)

    return clamp(
        supportQuality * centerQuality * aspectQuality * materialQuality * loadBonus * braceBonus,
        0,
        1
    )
}

function getMaterialSupportQuality(material) {
    const supportStrength = material.supportStrength || 1
    const friction = Math.max(0, Number(material.friction) || 0)
    const compressionStrength = Math.max(0, Number(material.compressionStrength) || 0)
    const shearStrength = Math.max(0, Number(material.shearStrength) || 0)
    const gripBonus = clamp(
        1
            + Math.max(0, friction - 0.5) * 0.3
            + Math.max(0, compressionStrength - 0.8) * 0.04
            + Math.max(0, shearStrength - 0.8) * 0.04,
        1,
        1.24
    )

    return supportStrength * gripBonus
}

export function getSupportedBlockIdsFromGraph(graph, options = {}) {
    const supported = new Set()
    const ignored = options.ignored || new Set()
    let changed = true

    while (changed) {
        changed = false

        for (const node of graph.nodes.values()) {
            if (ignored.has(node.id) || supported.has(node.id)) {
                continue
            }

            const supportEdges = getSupportEdges(node.id, graph, ignored)
            const hasSupportedBase = node.floorSupported
                || supportEdges.some((edge) => supported.has(edge.lowerId))

            if (hasSupportedBase && computeStability(node.id, graph, { ignored }) >= FALL_STABILITY_THRESHOLD) {
                supported.add(node.id)
                changed = true
            }
        }
    }

    return supported
}

export function predictVerticalImpact(removedBlockId, graph) {
    const falling = new Set([removedBlockId])
    let changed = true

    while (changed) {
        changed = false

        for (const node of graph.nodes.values()) {
            if (falling.has(node.id)) {
                continue
            }

            const supports = getSupportEdges(node.id, graph, falling)
            const directlyLostSupport = graph.edges.some((edge) => (
                edge.type === 'liesOn'
                && edge.upperId === node.id
                && falling.has(edge.lowerId)
            ))

            if (!directlyLostSupport && supports.length) {
                continue
            }

            const stability = computeStability(node.id, graph, { ignored: falling })
            if (stability < FALL_STABILITY_THRESHOLD) {
                falling.add(node.id)
                changed = true
            }
        }
    }

    falling.delete(removedBlockId)
    return falling
}

export function propagateHorizontalImpact(hitBlockId, force, direction, graph) {
    const forces = new Map()
    const queue = [hitBlockId]
    const hitNode = graph.nodes.get(hitBlockId)
    const normalizedDirection = normalizeDirection(direction)

    if (!hitNode) {
        return forces
    }

    forces.set(hitBlockId, Math.max(0, Number(force) || 0))

    while (queue.length) {
        const sourceId = queue.shift()
        const sourceForce = forces.get(sourceId) || 0
        const sourceNode = graph.nodes.get(sourceId)

        if (!sourceNode || sourceForce < 0.02) {
            continue
        }

        for (const neighbor of getImpactNeighbors(sourceId, graph)) {
            const targetNode = graph.nodes.get(neighbor.id)
            if (!targetNode) {
                continue
            }

            const material = MATERIALS[targetNode.material] || MATERIALS.wood
            const stability = computeStability(neighbor.id, graph)
            const directionFactor = getDirectionFactor(sourceNode, targetNode, normalizedDirection)
            const overlapFactor = clamp(neighbor.overlap / Math.max(WORLD.grid, Math.min(targetNode.width, targetNode.height)), 0.18, 1)
            const nextForce = sourceForce
                * overlapFactor
                * directionFactor
                * (1 - ((material.impactDamping ?? 0.72) * 0.28))
                / (1 + stability)
            const previousForce = forces.get(neighbor.id) || 0

            if (nextForce > previousForce + 0.01) {
                forces.set(neighbor.id, nextForce)
                queue.push(neighbor.id)
            }
        }
    }

    forces.delete(hitBlockId)
    return forces
}

function normalizeNode(item) {
    if (!item) {
        return null
    }

    const game = item.plugin?.game
    const id = game?.id || item.id
    const material = game?.material || item.material || 'wood'
    const shape = game?.shape || item.shape || null
    const angle = Number(item.angle ?? item.rotation ?? 0)
    const center = item.position
        ? { x: Number(item.position.x), y: Number(item.position.y) }
        : { x: Number(item.x), y: Number(item.y) }
    const width = Number(game?.width || item.width || getBoundsWidth(item.bounds) || BLOCK_SIZE.width)
    const height = Number(game?.height || item.height || getBoundsHeight(item.bounds) || BLOCK_SIZE.height)
    const bounds = item.bounds
        ? {
            minX: item.bounds.min.x,
            maxX: item.bounds.max.x,
            minY: item.bounds.min.y,
            maxY: item.bounds.max.y
        }
        : getRotatedBounds(center, width, height, angle)
    const area = Math.max(0.001, width * height)
    const mass = Number(item.mass) || area * ((MATERIALS[material] || MATERIALS.wood).density || 0.008)

    return {
        id,
        material,
        shape,
        angle,
        center,
        width,
        height,
        bounds,
        area,
        mass,
        floorSupported: false
    }
}

function getSupportEdges(blockId, graph, ignored = new Set()) {
    return graph.edges.filter((edge) => (
        edge.type === 'liesOn'
        && edge.upperId === blockId
        && !ignored.has(edge.lowerId)
    ))
}

function getSupportBase(node, supports) {
    return supports.reduce(
        (base, edge) => ({
            minX: Math.min(base.minX, edge.supportMinX),
            maxX: Math.max(base.maxX, edge.supportMaxX),
            overlap: base.overlap + edge.overlap
        }),
        { minX: Infinity, maxX: -Infinity, overlap: 0 }
    )
}

function getMassAbove(blockId, graph, ignored) {
    const seen = new Set()
    const queue = [blockId]
    let mass = 0

    while (queue.length) {
        const id = queue.shift()
        for (const edge of graph.edges) {
            if (edge.type !== 'liesOn' || edge.lowerId !== id || ignored.has(edge.upperId) || seen.has(edge.upperId)) {
                continue
            }
            seen.add(edge.upperId)
            queue.push(edge.upperId)
            mass += graph.nodes.get(edge.upperId)?.mass || 0
        }
    }

    return mass
}

function getSideBraceMass(blockId, graph, ignored) {
    let mass = 0

    for (const edge of graph.edges) {
        if (ignored.has(edge.leftId) || ignored.has(edge.rightId)) {
            continue
        }

        if (edge.type === 'touchesRight' && edge.leftId === blockId) {
            mass += graph.nodes.get(edge.rightId)?.mass || 0
        }

        if (edge.type === 'touchesLeft' && edge.rightId === blockId) {
            mass += graph.nodes.get(edge.leftId)?.mass || 0
        }
    }

    return mass
}

function getImpactNeighbors(sourceId, graph) {
    const neighbors = []

    for (const edge of graph.edges) {
        if (edge.type === 'liesOn') {
            if (edge.upperId === sourceId) {
                neighbors.push({ id: edge.lowerId, overlap: edge.overlap })
            } else if (edge.lowerId === sourceId) {
                neighbors.push({ id: edge.upperId, overlap: edge.overlap })
            }
        } else if (edge.type === 'touchesRight' && edge.leftId === sourceId) {
            neighbors.push({ id: edge.rightId, overlap: edge.overlap })
        } else if (edge.type === 'touchesLeft' && edge.rightId === sourceId) {
            neighbors.push({ id: edge.leftId, overlap: edge.overlap })
        }
    }

    return neighbors
}

function getDirectionFactor(source, target, direction) {
    const between = {
        x: target.center.x - source.center.x,
        y: target.center.y - source.center.y
    }
    const distance = Math.hypot(between.x, between.y)

    if (distance < 0.001) {
        return 0.62
    }

    const alignment = (between.x / distance) * direction.x + (between.y / distance) * direction.y
    return clamp(0.42 + Math.max(0, alignment) * 0.58, 0.28, 1)
}

function normalizeDirection(direction) {
    const length = Math.hypot(direction?.x || 0, direction?.y || 0)

    if (length < 0.001) {
        return { x: 1, y: 0 }
    }

    return {
        x: direction.x / length,
        y: direction.y / length
    }
}

function getRotatedBounds(center, width, height, angle) {
    const cos = Math.cos(angle)
    const sin = Math.sin(angle)
    const halfWidth = width / 2
    const halfHeight = height / 2
    const corners = [
        { x: -halfWidth, y: -halfHeight },
        { x: halfWidth, y: -halfHeight },
        { x: halfWidth, y: halfHeight },
        { x: -halfWidth, y: halfHeight }
    ].map((corner) => ({
        x: center.x + corner.x * cos - corner.y * sin,
        y: center.y + corner.x * sin + corner.y * cos
    }))

    return corners.reduce(
        (bounds, point) => ({
            minX: Math.min(bounds.minX, point.x),
            maxX: Math.max(bounds.maxX, point.x),
            minY: Math.min(bounds.minY, point.y),
            maxY: Math.max(bounds.maxY, point.y)
        }),
        { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
    )
}

function getBoundsWidth(bounds) {
    return bounds ? Math.abs(bounds.max.x - bounds.min.x) : 0
}

function getBoundsHeight(bounds) {
    return bounds ? Math.abs(bounds.max.y - bounds.min.y) : 0
}

function getIntervalOverlap(aMin, aMax, bMin, bMax) {
    return Math.max(0, Math.min(aMax, bMax) - Math.max(aMin, bMin))
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}
